import { FC } from 'react'

import {
  BankAccountsTransactionsWidgetDocument,
  BankAccountsTransactionsWidgetQuery,
  BankingTransactionFilter,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import {
  cleanNotFilteredMonths,
  generateMonthlyChartData,
  removeNullBetween,
} from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
  displayedFilters?: (string | string[])[]
}

const prepareData = (
  data: BankAccountsTransactionsWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const totalIncomeAmountCents = data?.banking?.incomeTransactions?.aggregate?.sum?.amountCents || 0

  const totalOutcomeAmountCents =
    data?.banking?.outcomeTransactions?.aggregate?.sum?.amountCents || 0

  const chartDataMap = generateMonthlyChartData(data)

  const barLabels = {
    income: { label: i18n.t('finance.overview.bank_accounts_transactions.income') },
    outcome: { label: i18n.t('finance.overview.bank_accounts_transactions.outcome') },
  }

  const countersChartDatas: ChartData[] = [
    {
      label: i18n.t('finance.overview.bank_accounts_transactions.total_income'),
      value: totalIncomeAmountCents,
      formattedValue: prepareDataArgs.amountFormatter(totalIncomeAmountCents),
    },
    {
      label: i18n.t('finance.overview.bank_accounts_transactions.total_outcome'),
      value: totalOutcomeAmountCents,
      formattedValue: prepareDataArgs.amountFormatter(totalOutcomeAmountCents),
    },
  ]

  return {
    countersChartDatas,
    groupedChartData: chartDataMap,
    barLabels,
    amountFormatter: prepareDataArgs.amountFormatter,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildBankAccountsTransactionsDashboardWidget = ({
  prepareDataArgs,
  filters,
}: {
  prepareDataArgs: PreparedDataArgsType
  filters: BankingTransactionFilter
}) => {
  const BankAccountsTransactionsDashboardWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { displayedFilters, countersChartDatas, groupedChartData, barLabels, amountFormatter } =
      data

    const cleanGroupedChartData = cleanNotFilteredMonths(
      groupedChartData,
      (current, key) => ({
        date: current.format('YYYY-MM-DD'),
        label: key,
        value: { income: 0, outcome: 0 },
      }),
      filters.bookingDate ?? filters.valueDate
    )

    return (
      <FinanceWidget
        id="bankAccountsTransactions"
        title={i18n.t('finance.overview.banks.cash-in-out')}
        displayedFilters={displayedFilters}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.BAR_CHART_STACKED_BY_SIGN}
        showChart
        countersChartDatas={countersChartDatas}
        chartDatas={cleanGroupedChartData}
        barChartConfig={{
          barLabels: barLabels,
        }}
        aspect="small"
      />
    )
  }

  const filteredTransactionsFilters = removeNullBetween(filters)

  return buildWidget({
    id: 'bankAccountsTransactionsDashboard',
    component: BankAccountsTransactionsDashboardWidget,
    document: BankAccountsTransactionsWidgetDocument,
    prepareData: (data: BankAccountsTransactionsWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      transactionsFilters: { ...filteredTransactionsFilters },
    },
  })
}

export const buildBankAccountsTransactionsInsightWidget = ({
  prepareDataArgs,
  filters,
}: {
  prepareDataArgs: PreparedDataArgsType
  filters: BankingTransactionFilter
}) => {
  const BankAccountsTransactionsInsightsWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas, groupedChartData, barLabels, amountFormatter } = data

    const cleanGroupedChartData = cleanNotFilteredMonths(
      groupedChartData,
      (current, key) => ({
        date: current.format('YYYY-MM-DD'),
        label: key,
        value: { income: 0, outcome: 0 },
      }),
      filters.bookingDate ?? filters.valueDate
    )

    return (
      <FinanceWidget
        id="bankAccountsTransactionsInsights"
        title={i18n.t('finance.overview.bank_accounts_transactions.title_account')}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.BAR_CHART_STACKED_BY_SIGN}
        showChart
        countersChartDatas={countersChartDatas}
        chartDatas={cleanGroupedChartData}
        barChartConfig={{
          barLabels: barLabels,
        }}
      />
    )
  }

  const filteredTransactionsFilters = removeNullBetween(filters)

  return buildWidget({
    id: 'bankAccountsTransactions',
    component: BankAccountsTransactionsInsightsWidget,
    document: BankAccountsTransactionsWidgetDocument,
    prepareData: (data: BankAccountsTransactionsWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      transactionsFilters: { ...filteredTransactionsFilters },
    },
  })
}
